import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import HistoryIcon from "@mui/icons-material/History";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { toast } from "react-toastify";
import { getMachineSafetyDetail, resolveMachineSafetyFileUrl } from "./machineSafetyApi";
import MachineSafetyHistoryDialog from "./MachineSafetyHistoryDialog";
import MachineSafetyMetricDialog from "./MachineSafetyMetricDialog";

const DRAWER_WIDTH = 340;

const MACHINE_SAFETY_FONT_SCOPE = {
  "&, & *": {
    fontFamily:
      '"Pretendard Variable", Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif !important',
    letterSpacing: "0 !important",
  },
  "& .MuiTypography-root": {
    fontWeight: "400 !important",
  },
  "& .MuiButton-root, & .MuiChip-label": {
    fontWeight: "700 !important",
    textTransform: "none !important",
  },
  "& .machine-safety-important": {
    fontWeight: "800 !important",
  },
};

const RISK = {
  critical: { bg: "#7f1d1d", fg: "#fff", soft: "#fee2e2", label: "매우 높음" },
  veryHigh: { bg: "#dc2626", fg: "#fff", soft: "#fee2e2", label: "높음" },
  high: { bg: "#f97316", fg: "#fff", soft: "#ffedd5", label: "높음" },
  elevated: { bg: "#f59e0b", fg: "#111827", soft: "#fef3c7", label: "주의" },
  caution: { bg: "#a3e635", fg: "#1f2937", soft: "#ecfccb", label: "보통" },
  safe: { bg: "#16a34a", fg: "#fff", soft: "#dcfce7", label: "낮음" },
  neutral: { bg: "#64748b", fg: "#fff", soft: "#f1f5f9", label: "미평가" },
};

const STATUS = {
  normal: { label: "정상", color: "#16a34a", bg: "rgba(22,163,74,.16)" },
  improving: { label: "개선 진행", color: "#f59e0b", bg: "rgba(245,158,11,.17)" },
  overdue: { label: "기한 초과", color: "#ef4444", bg: "rgba(239,68,68,.17)" },
  evaluation_required: { label: "평가 필요", color: "#38bdf8", bg: "rgba(56,189,248,.16)" },
};

const HAZARD_STATUS = {
  not_evaluated: "미평가",
  measure_required: "개선 필요",
  improving: "개선 중",
  improvement_completed: "개선 완료",
  evaluated: "평가 완료",
};

const dateLabel = (value) => (value ? String(value).slice(0, 10) : "-");

function Section({ icon, title, action, children, sx }) {
  return (
    <Box component="section" sx={{ px: 1.5, py: 2.15, ...sx }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.55 }}>
        <Stack direction="row" spacing={0.9} alignItems="center">
          <Box sx={{ color: "#a5b4fc", display: "grid", placeItems: "center" }}>{icon}</Box>
          <Typography className="machine-safety-important" sx={{ color: "#f8fafc", fontSize: 13.5, fontWeight: 650, lineHeight: 1.35 }}>{title}</Typography>
        </Stack>
        {action}
      </Stack>
      {children}
    </Box>
  );
}

function Metric({ icon, label, value, accent = "#60a5fa", onClick }) {
  return (
    <Box
      component={onClick ? "button" : "div"}
      type={onClick ? "button" : undefined}
      onClick={onClick}
      sx={{
        minWidth: 0,
        minHeight: 92,
        width: "100%",
        px: 1,
        py: 1.35,
        borderRadius: 1.5,
        bgcolor: "rgba(255,255,255,.055)",
        border: "1px solid rgba(255,255,255,.08)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        color: "inherit",
        font: "inherit",
        cursor: onClick ? "pointer !important" : "default",
        "& *": {
          cursor: onClick ? "pointer !important" : "inherit",
        },
        transition: "background-color .16s ease, border-color .16s ease, transform .16s ease",
        "&:hover": onClick
          ? {
              bgcolor: "rgba(255,255,255,.09)",
              borderColor: `${accent}88`,
              transform: "translateY(-1px)",
            }
          : undefined,
        "&:focus-visible": onClick
          ? { outline: `2px solid ${accent}`, outlineOffset: 2 }
          : undefined,
      }}
    >
      <Stack alignItems="center" spacing={0.45}>
        <Box sx={{ width: 28, height: 28, borderRadius: "50%", color: accent, bgcolor: `${accent}18`, display: "grid", placeItems: "center", "& svg": { fontSize: 17 } }}>{icon}</Box>
        <Typography sx={{ minWidth: 0, color: "#94a3b8", fontSize: 11.25, fontWeight: 500, lineHeight: 1.35 }}>{label}</Typography>
      </Stack>
      <Typography className="machine-safety-important" sx={{ mt: 0.65, color: "#fff", fontSize: 21, fontWeight: 850, lineHeight: 1.1, textAlign: "center" }}>{value}</Typography>
    </Box>
  );
}

function InfoRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.65 }}>
      <Typography sx={{ color: "#94a3b8", fontSize: 12 }}>{label}</Typography>
      <Typography sx={{ color: "#e2e8f0", fontSize: 12, fontWeight: 500, textAlign: "right", overflowWrap: "anywhere" }}>{value || "-"}</Typography>
    </Stack>
  );
}

function HazardRow({ item, onHistory }) {
  const tone = RISK[item.risk_tone] || RISK.neutral;
  return (
    <Paper
      component="button"
      type="button"
      onClick={() => onHistory(item.register_cause_id)}
      elevation={0}
      sx={{
        width: "100%",
        p: 1.25,
        textAlign: "left",
        font: "inherit",
        color: "inherit",
        bgcolor: "rgba(255,255,255,.045)",
        border: "1px solid rgba(255,255,255,.08)",
        borderRadius: 1.6,
        cursor: "pointer",
        transition: "background-color .16s ease, border-color .16s ease",
        "&:hover": { bgcolor: "rgba(255,255,255,.085)", borderColor: "rgba(129,140,248,.55)" },
      }}
    >
      <Stack direction="row" spacing={1.05} alignItems="flex-start">
        <Box sx={{ width: 5, alignSelf: "stretch", minHeight: 40, borderRadius: 3, bgcolor: tone.bg, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography className="machine-safety-important" sx={{ color: "#f8fafc", fontSize: 12.25, fontWeight: 600, lineHeight: 1.5, overflowWrap: "anywhere" }}>
            {item.danger_description}
          </Typography>
          <Stack direction="row" spacing={0.55} flexWrap="wrap" useFlexGap sx={{ mt: 0.9 }}>
            <Chip size="small" label={item.process_name || "공정 미지정"} sx={{ height: 22, color: "#cbd5e1", bgcolor: "rgba(148,163,184,.13)", fontSize: 10.5 }} />
            <Chip size="small" label={item.risk_score == null ? "미평가" : `${item.effective_risk_score}점`} sx={{ height: 22, bgcolor: tone.bg, color: tone.fg, fontWeight: 700, fontSize: 10.5 }} />
            <Chip size="small" label={HAZARD_STATUS[item.status] || "확인 필요"} variant="outlined" sx={{ height: 22, color: item.status === "improvement_completed" ? "#4ade80" : "#fbbf24", borderColor: "currentColor", fontSize: 10.5 }} />
          </Stack>
        </Box>
        <ChevronRightIcon sx={{ mt: 0.5, color: "#64748b", fontSize: 18 }} />
      </Stack>
    </Paper>
  );
}

export default function MachineSafetyDrawer({
  open = true,
  onClose,
  groupCode,
  projectCode,
  linkId,
  variant = "temporary",
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryableError, setRetryableError] = useState(false);
  const [data, setData] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyCauseId, setHistoryCauseId] = useState(null);
  const [metricDialog, setMetricDialog] = useState({ open: false, type: null, items: [] });
  const [machineExpanded, setMachineExpanded] = useState(false);
  const requestSequenceRef = useRef(0);

  const load = useCallback(async () => {
    const requestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestSequence;
    if (!linkId) {
      setLoading(false);
      setData(null);
      setError("선택된 설비 정보가 없습니다.");
      setRetryableError(false);
      return;
    }

    setLoading(true);
    setError("");
    setRetryableError(false);
    try {
      const result = await getMachineSafetyDetail({ groupCode, projectCode, linkId });
      if (requestSequence !== requestSequenceRef.current) return;
      setData(result || null);
    } catch (requestError) {
      if (requestSequence !== requestSequenceRef.current) return;
      const responseStatus = requestError?.response?.status;
      setError(requestError?.response?.data?.message || "설비 안전정보를 불러오지 못했습니다.");
      setRetryableError(!responseStatus || responseStatus >= 500);
      setData(null);
    } finally {
      if (requestSequence === requestSequenceRef.current) setLoading(false);
    }
  }, [groupCode, linkId, projectCode]);

  useEffect(() => {
    if (open) load();
    return () => {
      requestSequenceRef.current += 1;
    };
  }, [load, open]);

  const machine = data?.machine && typeof data.machine === "object" ? data.machine : {};
  const summaryData = data?.summary && typeof data.summary === "object" ? data.summary : {};
  const summary = {
    overall_status: "evaluation_required",
    highest_risk_tone: "neutral",
    hazard_count: 0,
    evaluated_count: 0,
    improving_count: 0,
    evaluation_rate: 0,
    highest_risk_score: null,
    ...summaryData,
  };
  const lookup = data?.lookup && typeof data.lookup === "object" ? data.lookup : {};
  const hazards = Array.isArray(data?.hazards)
    ? data.hazards.filter(
        (item) => item && typeof item === "object" && item.register_cause_id
      )
    : [];
  const msdsList = Array.isArray(data?.msds)
    ? data.msds.filter((item) => item && typeof item === "object")
    : [];
  const locations = useMemo(
    () => (Array.isArray(data?.locations) ? data.locations : [])
      .map((item) => item?.location_name)
      .filter(Boolean)
      .join(", "),
    [data?.locations]
  );
  const processes = useMemo(
    () => (Array.isArray(data?.processes) ? data.processes : [])
      .map((item) => item?.process_name)
      .filter(Boolean)
      .join(", "),
    [data?.processes]
  );
  const priorityItem = data?.priority_item && typeof data.priority_item === "object"
    ? data.priority_item
    : null;
  const hasMachineData = Boolean(data && machine.machine_id);
  const status = STATUS[summary.overall_status] || STATUS.evaluation_required;
  const risk = RISK[summary.highest_risk_tone] || RISK.neutral;

  const showHistory = (causeId = null) => {
    setHistoryCauseId(causeId);
    setHistoryOpen(true);
  };

  const showMetricDetail = (type) => {
    const metricItems = {
      hazards,
      evaluated: hazards.filter((item) => Boolean(item.eval_id)),
      improving: hazards.filter((item) => item.status === "improving"),
      highestRisk: hazards.filter(
        (item) =>
          summary.highest_risk_score > 0 &&
          Number(item.effective_risk_score) === Number(summary.highest_risk_score)
      ),
    };
    const emptyMessages = {
      hazards: "현재 주기에 등록된 유해·위험요인이 없습니다.",
      evaluated: "현재 주기에 평가 완료된 항목이 없습니다.",
      improving: "현재 진행 중인 개선 항목이 없습니다.",
      highestRisk: "현재 주기에 표시할 위험성 평가 결과가 없습니다.",
    };
    const items = metricItems[type] || [];

    if (!items.length) {
      toast.info(emptyMessages[type] || "표시할 정보가 없습니다.");
      return;
    }

    setMetricDialog({ open: true, type, items });
  };

  const openInSafetySystem = (item = null) => {
    const params = new URLSearchParams({ source: "dt" });
    if (machine.machine_id) params.set("machine_id", machine.machine_id);
    if (machine.display_name) params.set("machine_name", machine.display_name);
    let path;
    if (item?.register_cause_id) {
      params.set("register_cause_id", item.register_cause_id);
      path = `/safe/${groupCode}/${projectCode}/assessment/wizard/step-1?${params.toString()}`;
    } else {
      path = `/safe/${groupCode}/${projectCode}/register/cause?${params.toString()}`;
    }
    window.open(path, "_blank", "noopener,noreferrer");
  };

  const drawerPaperSx = {
    ...MACHINE_SAFETY_FONT_SCOPE,
    width: `min(100vw, ${DRAWER_WIDTH}px)`,
    maxWidth: "100vw",
    boxSizing: "border-box",
    border: 0,
    color: "#fff",
    bgcolor: "#0b1220",
    backgroundImage: "linear-gradient(180deg, rgba(15,23,42,.98), rgba(4,10,20,.985))",
    boxShadow: "-18px 0 46px rgba(0,0,0,.34)",
    pointerEvents: "auto",
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        variant={variant}
        ModalProps={{
          keepMounted: true,
          hideBackdrop: true,
          disableAutoFocus: true,
          disableEnforceFocus: true,
          disableRestoreFocus: true,
          sx: { pointerEvents: "none" },
        }}
        PaperProps={{ sx: drawerPaperSx }}
      >
        <Box sx={{ minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ px: 1.75, pt: 2.15, pb: 1.95, borderBottom: "1px solid rgba(255,255,255,.09)", bgcolor: "rgba(2,6,23,.78)" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Stack direction="row" spacing={1.15} alignItems="center" sx={{ minWidth: 0 }}>
                <Box sx={{ width: 42, height: 42, borderRadius: 1.8, bgcolor: "rgba(99,102,241,.2)", border: "1px solid rgba(129,140,248,.22)", color: "#a5b4fc", display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <PrecisionManufacturingOutlinedIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap className="machine-safety-important" sx={{ color: "#fff", fontSize: 18.5, fontWeight: 800, lineHeight: 1.25 }}>
                    {loading ? "설비 안전정보" : machine.display_name || "설비 정보 없음"}
                  </Typography>
                  <Typography noWrap sx={{ mt: 0.35, color: "#94a3b8", fontSize: 12 }}>
                    {machine.machine_manage_code || "관리번호 미등록"} · 연동 ID {machine.dt_sync_id || linkId || "-"}
                  </Typography>
                </Box>
              </Stack>
              {variant !== "permanent" && <IconButton aria-label="닫기" onClick={onClose} sx={{ color: "#cbd5e1" }}><CloseIcon /></IconButton>}
            </Stack>
            {!loading && hasMachineData && (
              <Stack direction="row" spacing={0.65} flexWrap="wrap" useFlexGap sx={{ mt: 1.65 }}>
                <Chip size="small" label={status.label} sx={{ height: 25, bgcolor: status.bg, color: status.color, border: `1px solid ${status.color}55`, fontWeight: 700 }} />
                <Chip icon={<PlaceOutlinedIcon />} size="small" label={locations || "장소 미배정"} sx={{ height: 25, maxWidth: "100%", color: "#cbd5e1", bgcolor: "rgba(255,255,255,.065)", border: "1px solid rgba(255,255,255,.06)", "& .MuiChip-icon": { color: "#94a3b8" } }} />
                <Chip icon={<AccountTreeOutlinedIcon />} size="small" label={processes || "공정 미연결"} sx={{ height: 25, maxWidth: "100%", color: "#cbd5e1", bgcolor: "rgba(255,255,255,.065)", border: "1px solid rgba(255,255,255,.06)", "& .MuiChip-icon": { color: "#94a3b8" } }} />
              </Stack>
            )}
          </Box>

          <Box sx={{ minHeight: 0, flex: 1, overflowY: "auto", overflowX: "hidden", pt: 0.7, pb: 2, scrollbarWidth: "thin", scrollbarColor: "#475569 transparent", "&::-webkit-scrollbar": { width: 6 }, "&::-webkit-scrollbar-thumb": { bgcolor: "#475569", borderRadius: 8 } }}>
            {loading && (
              <Stack spacing={1.25} sx={{ p: 1.5 }}>
                <Skeleton variant="rounded" height={92} sx={{ bgcolor: "rgba(255,255,255,.08)" }} />
                <Skeleton variant="rounded" height={150} sx={{ bgcolor: "rgba(255,255,255,.08)" }} />
                <Skeleton variant="rounded" height={220} sx={{ bgcolor: "rgba(255,255,255,.08)" }} />
              </Stack>
            )}
            {!loading && !hasMachineData && (
              <Box
                sx={{
                  minHeight: "100%",
                  px: 3,
                  py: 6,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    mb: 1.75,
                    borderRadius: 2,
                    display: "grid",
                    placeItems: "center",
                    color: "#fda4af",
                    bgcolor: "rgba(244,63,94,.12)",
                    border: "1px solid rgba(251,113,133,.22)",
                  }}
                >
                  <WarningAmberRoundedIcon sx={{ fontSize: 27 }} />
                </Box>
                <Typography
                  className="machine-safety-important"
                  sx={{ color: "#f8fafc", fontSize: 16, fontWeight: 700, lineHeight: 1.45 }}
                >
                  설비 정보를 찾을 수 없습니다.
                </Typography>
                <Typography sx={{ mt: 0.75, maxWidth: 250, color: "#94a3b8", fontSize: 12, lineHeight: 1.6 }}>
                  {error || "선택한 DT 설비와 연결된 안전관리 설비가 없습니다."}
                </Typography>
                {retryableError && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RefreshRoundedIcon />}
                    onClick={load}
                    sx={{ mt: 2, color: "#c7d2fe", borderColor: "rgba(165,180,252,.42)" }}
                  >
                    다시 불러오기
                  </Button>
                )}
              </Box>
            )}
            {!loading && hasMachineData && (
              <>
                <Section title="현재 주기 안전 현황" icon={<FactCheckOutlinedIcon fontSize="small" />} action={<Typography sx={{ color: "#64748b", fontSize: 10.5 }}>{lookup.period_label || lookup.month_start?.slice(0, 7) || ""}</Typography>}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
                    <Metric icon={<WarningAmberRoundedIcon fontSize="small" />} label="유해·위험요인" value={`${summary.hazard_count || 0}건`} accent="#fbbf24" onClick={() => showMetricDetail("hazards")} />
                    <Metric icon={<VerifiedOutlinedIcon fontSize="small" />} label="평가 완료" value={`${summary.evaluated_count || 0}건`} accent="#4ade80" onClick={() => showMetricDetail("evaluated")} />
                    <Metric icon={<BuildCircleOutlinedIcon fontSize="small" />} label="개선 진행" value={`${summary.improving_count || 0}건`} accent="#fb923c" onClick={() => showMetricDetail("improving")} />
                    <Metric icon={<WarningAmberRoundedIcon fontSize="small" />} label="최고 위험성" value={summary.highest_risk_score ? `${summary.highest_risk_score}점` : "-"} accent={risk.bg} onClick={() => showMetricDetail("highestRisk")} />
                  </Box>
                </Section>

                {priorityItem && (
                  <Section title="우선 확인 항목" icon={<WarningAmberRoundedIcon fontSize="small" />}>
                    <Box sx={{ p: 1.35, borderRadius: 1.7, bgcolor: "rgba(255,255,255,.055)", border: `1px solid ${risk.bg}66`, borderLeft: `4px solid ${risk.bg}`, boxShadow: "0 8px 20px rgba(0,0,0,.1)" }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={0.8}>
                        <Chip size="small" label={priorityItem.process_name || "공정 미지정"} sx={{ height: 22, color: "#cbd5e1", bgcolor: "rgba(148,163,184,.14)", fontSize: 10.5, fontWeight: 500 }} />
                        <Chip size="small" label={priorityItem.effective_risk_score ? `${priorityItem.effective_risk_score}점` : "미평가"} sx={{ height: 23, flexShrink: 0, bgcolor: risk.bg, color: risk.fg, fontWeight: 700, fontSize: 10.75 }} />
                      </Stack>
                      <Typography className="machine-safety-important" sx={{ mt: 1, color: "#f8fafc", fontSize: 12.5, fontWeight: 600, lineHeight: 1.55, overflowWrap: "anywhere" }}>
                        {priorityItem.danger_description || "확인할 위험요인 내용이 없습니다."}
                      </Typography>
                      {priorityItem.expected_date && (
                        <Typography sx={{ mt: 0.75, color: "#94a3b8", fontSize: 11.25, fontWeight: 400 }}>
                          개선 예정일 {dateLabel(priorityItem.expected_date)}
                        </Typography>
                      )}
                      <Button
                        fullWidth
                        size="small"
                        variant="outlined"
                        endIcon={<OpenInNewIcon />}
                        onClick={() => openInSafetySystem(priorityItem)}
                        sx={{ mt: 1.1, minHeight: 32, color: "#c7d2fe", borderColor: "rgba(165,180,252,.45)", fontSize: 11 }}
                      >
                        안전관리 시스템에서 보기
                      </Button>
                    </Box>
                  </Section>
                )}

                <Section
                  title="위험성평가"
                  icon={<HistoryIcon fontSize="small" />}
                  action={<Button size="small" onClick={() => showHistory()} endIcon={<ChevronRightIcon />} sx={{ color: "#a5b4fc", minWidth: 0, px: 0.6, fontSize: 11 }}>전체 이력</Button>}
                >
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.65 }}>
                    <Typography sx={{ color: "#94a3b8", fontSize: 11.5 }}>평가 진행률</Typography>
                    <Typography className="machine-safety-important" sx={{ color: "#e2e8f0", fontSize: 11.5, fontWeight: 600 }}>{summary.evaluated_count || 0}/{summary.hazard_count || 0}건 · {summary.evaluation_rate || 0}%</Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={Number(summary.evaluation_rate) || 0} sx={{ height: 6, borderRadius: 9, bgcolor: "rgba(255,255,255,.08)", "& .MuiLinearProgress-bar": { borderRadius: 9, bgcolor: "#6366f1" } }} />
                  <Stack spacing={0.85} sx={{ mt: 1.25 }}>
                    {hazards.length ? hazards.map((item) => <HazardRow key={item.register_cause_id || item.eval_id || item.danger_description} item={item} onHistory={showHistory} />) : <Typography sx={{ color: "#94a3b8", fontSize: 12 }}>연결된 유해·위험요인이 없습니다.</Typography>}
                  </Stack>
                </Section>

                <Divider sx={{ borderColor: "rgba(255,255,255,.07)" }} />
                <Section title="사용 물질안전보건자료" icon={<ScienceOutlinedIcon fontSize="small" />}>
                  <Stack spacing={0.75}>
                    {msdsList.length ? msdsList.map((item) => (
                      <Box key={item.id} sx={{ p: 1.2, borderRadius: 1.5, bgcolor: "rgba(255,255,255,.045)", border: "1px solid rgba(255,255,255,.07)" }}>
                        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                          <Box sx={{ minWidth: 0 }}>
                            <Typography noWrap className="machine-safety-important" sx={{ color: "#f1f5f9", fontSize: 12.5, fontWeight: 600 }}>{item.product_name}</Typography>
                            <Typography noWrap sx={{ mt: 0.2, color: "#94a3b8", fontSize: 10.8 }}>{item.substance_name || "물질명 미등록"} · 만료 {dateLabel(item.expire_at)}</Typography>
                          </Box>
                          {item.pdf_url && <Tooltip title="원본 PDF 열기"><IconButton component="a" href={resolveMachineSafetyFileUrl(item.pdf_url)} target="_blank" rel="noreferrer" size="small" sx={{ color: "#a5b4fc" }}><OpenInNewIcon fontSize="inherit" /></IconButton></Tooltip>}
                        </Stack>
                      </Box>
                    )) : <Typography sx={{ color: "#94a3b8", fontSize: 12 }}>이 설비에 연결된 MSDS가 없습니다.</Typography>}
                  </Stack>
                </Section>

                <Divider sx={{ borderColor: "rgba(255,255,255,.07)" }} />
                <Section
                  title="설비 기본정보"
                  icon={<PrecisionManufacturingOutlinedIcon fontSize="small" />}
                  action={<IconButton aria-label={machineExpanded ? "접기" : "펼치기"} size="small" onClick={() => setMachineExpanded((value) => !value)} sx={{ color: "#94a3b8", transform: machineExpanded ? "rotate(180deg)" : "none", transition: "transform .2s" }}><ExpandMoreIcon fontSize="small" /></IconButton>}
                >
                  <Collapse in={machineExpanded} unmountOnExit>
                    <Box sx={{ px: 1.2, py: 0.7, bgcolor: "rgba(255,255,255,.035)", borderRadius: 1.5 }}>
                      <InfoRow label="제작사" value={machine.machine_manufacturer} />
                      <InfoRow label="모델명" value={machine.machine_model_name} />
                      <InfoRow label="규격" value={machine.machine_standard} />
                      <InfoRow label="구입일" value={machine.machine_purchase_date} />
                      <InfoRow label="크기" value={machine.machine_size} />
                      <InfoRow label="전압" value={machine.machine_voltage} />
                    </Box>
                  </Collapse>
                </Section>
              </>
            )}
          </Box>

          {hasMachineData && (
            <Box sx={{ p: 1.2, borderTop: "1px solid rgba(255,255,255,.08)", bgcolor: "rgba(2,6,23,.92)" }}>
              <Stack direction="row" spacing={0.75}>
                <Button fullWidth size="small" variant="outlined" startIcon={<HistoryIcon />} onClick={() => showHistory()} sx={{ minHeight: 38, color: "#c7d2fe", borderColor: "rgba(165,180,252,.45)", whiteSpace: "nowrap", fontSize: 11.5 }}>평가 이력</Button>
                <Button fullWidth size="small" variant="contained" endIcon={<OpenInNewIcon />} onClick={() => openInSafetySystem()} sx={{ minHeight: 38, bgcolor: "#6366f1", whiteSpace: "nowrap", fontSize: 11.5, "&:hover": { bgcolor: "#4f46e5" } }}>위험요인 관리</Button>
              </Stack>
            </Box>
          )}
        </Box>
      </Drawer>

      <MachineSafetyHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        groupCode={groupCode}
        projectCode={projectCode}
        linkId={linkId}
        machineName={machine.display_name}
        initialCauseId={historyCauseId}
      />
      <MachineSafetyMetricDialog
        open={metricDialog.open}
        onClose={() => setMetricDialog({ open: false, type: null, items: [] })}
        type={metricDialog.type}
        items={metricDialog.items}
        machineName={machine.display_name}
        periodLabel={lookup.period_label || lookup.month_start?.slice(0, 7) || ""}
        onOpenSafety={openInSafetySystem}
      />
    </>
  );
}
